import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Ladro disponibili al level-up nei livelli 6-10
 * (manuale V1, rr. 3905-3998). 14 mosse totali.
 *
 * Manuale V1, r. 170: dal 6° livello in poi il giocatore può scegliere
 * anche dal pool 2-5 (oltre a queste). Lo schema lo modella come pool
 * separato in `ClassDefinition.advancedMoves6to10`; la regola "puoi anche
 * pescare dal 2-5" la implementa la logica di level-up applicativa.
 *
 * Catene presenti:
 *  - *Combattente Sleale* SOSTITUISCE *Colpo Basso*
 *  - *Estremamente Prudente* SOSTITUISCE *Prudente*
 *  - *Danzare tra le Lame* RICHIEDE *Schivata*
 *  - *Fidati di Me* RICHIEDE *Arte Persuasiva*
 *  - *Tutto Va Secondo i Miei Piani* RICHIEDE *Ingegneria Sociale*
 *  - *Alchimia Sperimentale* RICHIEDE *Alchimista*
 */

const combattenteSleale: Move = {
  id: 'ladro.combattente-sleale',
  name: 'Combattente Sleale',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: "Quando infliggi danni usando un'arma con etichetta *precisa* o con portata *corta*",
  description:
    'Se lo fai con *Attacco Furtivo*, infliggi +1d8 danni, altrimenti infliggi +1d4 danni.',
  tags: [],
  prerequisites: [],
  replaces: ['ladro.colpo-basso'],
};

const cecchino: Move = {
  id: 'ladro.cecchino',
  name: 'Cecchino',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando fai una *raffica* contro un bersaglio ignaro della tua presenza',
  description: 'Non riveli mai la tua posizione, neanche con un 6-.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const danzareTraLeLame: Move = {
  id: 'ladro.danzare-tra-le-lame',
  name: 'Danzare tra le Lame',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando *tagli e spacchi*',
  description:
    "Con un 10+, se *non* lasci che il nemico risponda, infliggi il tuo dado di Danno e la prossima volta che usi *Schivata* per evitare un attacco in mischia di quell'avversario consideri di aver ottenuto un 10+, senza bisogno di tirare.",
  tags: [],
  prerequisites: ['ladro.schivata'],
  replaces: [],
};

const viaDiFuga: Move = {
  id: 'ladro.via-di-fuga',
  name: 'Via di Fuga',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando sei nei guai fino al collo e hai bisogno di levarti di torno',
  description:
    'Indica la tua via di fuga e tira+**DES**. Con 10+, scompari. Con 7-9, puoi restare o andartene, ma se vai ti costerà: il GM ti dirà chi o cosa viene via con te, o qualcosa che hai dovuto lasciare indietro.',
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Scompari.' },
    on7to9: {
      text: 'Puoi restare o andartene; se vai, paghi un costo (chi/cosa viene con te o cosa lasci).',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const ilCuoreOltreLOstacolo: Move = {
  id: 'ladro.il-cuore-oltre-l-ostacolo',
  name: "Il Cuore oltre l'Ostacolo",
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando *sfidi il pericolo*',
  description:
    'Con un 12+, trascendi i tuoi limiti. Non solo riesci nel tuo intento, ma il GM ti offrirà un esito migliore e un momento di eleganza o un attimo di pura bellezza.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const estremamentePrudente: Move = {
  id: 'ladro.estremamente-prudente',
  name: 'Estremamente Prudente',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando usi *Esperto di Trappole*',
  description:
    "Hai sempre 1 presa in più, anche con 6-. Con 12+, inoltre, la prima volta che scopri una trappola in una certa area usando una presa, il GM ti dirà gratuitamente cos'è, cosa la attiva, chi l'ha piazzata e come puoi usarla a tuo vantaggio.",
  tags: [],
  prerequisites: [],
  replaces: ['ladro.prudente'],
};

const smanettoneMagico: Move = {
  id: 'ladro.smanettone-magico',
  name: 'Smanettone Magico',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger:
    'Quando cerchi di capire il funzionamento di un oggetto o di un congegno magico grazie alla tua esperienza sul campo',
  description:
    "Tira+**DES**. Con 7+, capisci in linea generale come funziona: il GM te lo spiegherà approssimativamente e senza tecnicismi magici. Con 10+, scegli 2. Con 7-9, scegli 1 tra le seguenti opzioni:\n\n- non ti è sfuggito un particolare di una certa rilevanza;\n- ti sei fatto un'idea di come attivarlo;\n- non ti metti in pericolo o attiri attenzioni indesiderate.\n\nUna volta che hai capito il funzionamento di un oggetto magico, potrai usarlo anche se non rispetti eventuali requisiti previsti. Tuttavia, finché non ci avrai fatto un po' di pratica (a discrezione del GM), l'utilizzo potrebbe causare degli effetti collaterali.",
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Capisci come funziona + scegli 2 opzioni.' },
    on7to9: { text: 'Capisci come funziona + scegli 1 opzione.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const camuffarsi: Move = {
  id: 'ladro.camuffarsi',
  name: 'Camuffarsi',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando hai qualche minuto per creare un travestimento',
  description:
    "Spiega come ti vuoi camuffare e definisci con il GM se hai i materiali necessari. In caso affermativo, chiunque ti veda crederà che tu sia un'altra creatura, approssimativamente della tua stessa forma e dimensione. Le tue azioni e la tua voce possono farti scoprire, ma il tuo aspetto no, mai.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const falsario: Move = {
  id: 'ladro.falsario',
  name: 'Falsario',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: "Quando hai il tempo e la tranquillità necessari per creare un'imitazione di qualcosa",
  description:
    "(documento, oggetto, ecc.), concorda con il GM che materiali ti occorrono e poi tira+**DES**. Con 10+, l'imitazione è esattamente come dev'essere. Con 7-9, sembra ciò che volevi, almeno a un primo controllo superficiale. Con 6- hai fatto un errore grossolano, ma te ne accorgerai solo quando sarà troppo tardi!",
  roll: {
    stat: 'destrezza',
    on10plus: { text: "L'imitazione è esattamente come dev'essere." },
    on7to9: { text: 'Sembra ciò che volevi, almeno a un primo controllo superficiale.' },
    on6minus: { text: 'Errore grossolano: te ne accorgerai solo quando sarà troppo tardi!' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const fidatiDiMe: Move = {
  id: 'ladro.fidati-di-me',
  name: 'Fidati di Me',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando usi la tua *Arte Persuasiva*',
  description: 'Con un 10+, scegli 2 opzioni dalla lista. Con un 7-9, scegline 1.',
  tags: [],
  prerequisites: ['ladro.arte-persuasiva'],
  replaces: [],
};

const tuttoVaSecondoIMieiPiani: Move = {
  id: 'ladro.tutto-va-secondo-i-miei-piani',
  name: 'Tutto Va Secondo i Miei Piani',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Quando hai bisogno che qualcuno abbia fatto qualcosa per te',
  description:
    'Lo avevi di certo pianificato! Puoi spendere una presa di *Ingegneria Sociale* per incappare in una fortunata ma plausibile coincidenza (una porta è rimasta aperta, qualcuno ha detto che lo straniero che sta per arrivare è un tipo a posto e così via).',
  tags: [],
  prerequisites: ['ladro.ingegneria-sociale'],
  replaces: [],
};

const colpoGrosso: Move = {
  id: 'ladro.colpo-grosso',
  name: 'Colpo Grosso',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger:
    "Quando passi del tempo (almeno un giorno) a preparare un piano per un'impresa criminale importante",
  description:
    "Descrivine la natura, i suoi obiettivi e quali pensi che possano essere le conseguenze in caso di fallimento. Quindi fai al GM tutte le seguenti domande:\n\n- qual è l'ostacolo principale alla riuscita?\n- Quale persona o fazione è più probabile che si intrometta?\n- Cos'altro potrebbe succedere se il piano fallisce?\n- Chi ci rimetterebbe di più se il piano va in porto?\n\nLa prima volta che agisci in base alle risposte, prendi +1 al tiro.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const alchimiaSperimentale: Move = {
  id: 'ladro.alchimia-sperimentale',
  name: 'Alchimia Sperimentale',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger:
    'Quando hai qualche ora di tempo e un posto sicuro per provare a preparare un nuovo tipo di veleno',
  description:
    "Descrivi quali effetti vorresti che avesse e concorda con il GM gli ingredienti necessari. Il GM, in relazione agli effetti desiderati, definisce una o più limitazioni per il nuovo preparato:\n\n- funziona soltanto in determinate circostanze;\n- il meglio che riesci a fare è una versione più debole;\n- ci mette un po' di tempo a fare effetto;\n- ha degli effetti collaterali ben visibili.\n\nQuindi tira+**DES**. Con 10+, ottieni 3 utilizzi del nuovo veleno. Con 12+, inoltre, dopo averlo usato almeno una volta, potrai prepararlo ancora con *Alchimista* usando gli stessi ingredienti, ma senza le limitazioni del prototipo. Con 7-9, invece, ne ottieni 1 sola dose. Con 6-, ti sei avvelenato da solo!\n\nTutti i veleni sperimentali hanno l'etichetta *pericoloso*, anche se li riproduci con *Alchimista*.",
  roll: {
    stat: 'destrezza',
    on12plus: {
      text: 'Ottieni 3 utilizzi + dopo il primo uso, riproducibile con Alchimista senza limitazioni.',
    },
    on10plus: { text: 'Ottieni 3 utilizzi del nuovo veleno.' },
    on7to9: { text: 'Ottieni 1 sola dose.' },
    on6minus: { text: 'Ti sei avvelenato da solo!' },
  },
  tags: [],
  prerequisites: ['ladro.alchimista'],
  replaces: [],
};

const furfanteMulticlasse: Move = {
  id: 'ladro.furfante-multiclasse',
  name: 'Furfante Multiclasse',
  type: 'advanced_6_10',
  classId: 'ladro',
  trigger: 'Al level-up (livello 6-10), come scelta multiclasse',
  description:
    "Mossa *multiclasse*. Scegli una mossa non multiclasse da un'altra classe. Ai fini della scelta, considera il tuo livello come fosse inferiore di uno.",
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-17 (D7): pool aperto + cap livello -1.
  multiclass: {
    picks: { kind: 'any-other-class' },
    levelAdjustment: { delta: -1 },
  },
};

export const ladroAdvanced6to10Moves: readonly Move[] = [
  combattenteSleale,
  cecchino,
  danzareTraLeLame,
  viaDiFuga,
  ilCuoreOltreLOstacolo,
  estremamentePrudente,
  smanettoneMagico,
  camuffarsi,
  falsario,
  fidatiDiMe,
  tuttoVaSecondoIMieiPiani,
  colpoGrosso,
  alchimiaSperimentale,
  furfanteMulticlasse,
] as const;
